import {
  TLeadOpenViewFieldValues,
  TLeadOpenViewSyncData,
} from "types/openview.type";
import { AccountLeadStore } from "store/UserAccounts/UserAccount/AccountLeads/AccountLead.store";
import { FC, useCallback, useEffect, useMemo, useState } from "react";
import { observer } from "mobx-react";
import {
  getArchivedState,
  getEcosystemOpenViewSegmentedAddress,
  getLocalOpenViewSegmentedAddress,
} from "utils/openview.utils";
import { yupResolver } from "@hookform/resolvers/yup";
import { useForm } from "react-hook-form";
import { ActionPromptContainer, Error, LeadTypeTag, Loader } from "components";
import { Box, Button, Divider } from "@chakra-ui/react";
import { LeadTypeEnum } from "enums/lead-type.enum";
import { OpenviewValidationSchema } from "utils/validation-schemas/openview-sync.validation";
import {
  AccountIntegrationOpenViewContactsStore,
  openViewNewContactValue,
} from "store/UserAccounts/UserAccount/AccountIntegrations/AccountIntegrationOpenView/AccountIntegrationOpenViewContacts.store";
import { ApiRequestStatusEnum } from "enums/api-request-status.enum";
import { getUserFullName } from "utils/account-user.utils";
import { LeadAddressForm } from "./LeadAddressForm";
import { ExistingRecordsForm } from "./ExistingRecordsForm";

type TFieldValues = TLeadOpenViewFieldValues;

interface IProps {
  leadStore: AccountLeadStore;
  handleSyncLead: (syncData: TLeadOpenViewSyncData) => Promise<void>;
  closePrompt: () => void;
  contactsStore: AccountIntegrationOpenViewContactsStore;
}

export const LeadOpenViewSyncFormPrompt: FC<IProps> = observer(
  ({ leadStore, handleSyncLead, closePrompt, contactsStore }) => {
    const {
      address,
      postcode,
      type,
      id: leadId,
      person,
      ecosystemAddress,
    } = leadStore.lead;
    const { validateLeadAddressOnEcosystem } = leadStore;
    const {
      openViewRelatedContactsOptions,
      fetchOpenViewAccountContacts,
    } = contactsStore;
    const [ecosystemLoadingState, setEcosystemLoadingState] = useState<
      ApiRequestStatusEnum
    >(ApiRequestStatusEnum.NONE);

    const [loadingState, setLoadingState] = useState(ApiRequestStatusEnum.NONE);

    const transformedAddress = useMemo(() => {
      if (ecosystemLoadingState === ApiRequestStatusEnum.ERROR) {
        return getLocalOpenViewSegmentedAddress(address, postcode);
      }
      if (!ecosystemAddress) return;
      return getEcosystemOpenViewSegmentedAddress(
        ecosystemAddress,
        address,
        postcode
      );
    }, [ecosystemAddress, address, postcode, ecosystemLoadingState]);

    useEffect(() => {
      const fetchData = async () => {
        try {
          setEcosystemLoadingState(ApiRequestStatusEnum.PENDING);
          await validateLeadAddressOnEcosystem();
          setEcosystemLoadingState(ApiRequestStatusEnum.SUCCESS);
        } catch (error) {
          setEcosystemLoadingState(ApiRequestStatusEnum.ERROR);
        }
      };
      fetchData();
    }, [validateLeadAddressOnEcosystem]);

    useEffect(() => {
      const fetchData = async () => {
        try {
          setLoadingState(ApiRequestStatusEnum.PENDING);
          await fetchOpenViewAccountContacts(leadId);
          setLoadingState(ApiRequestStatusEnum.SUCCESS);
        } catch (error) {
          setLoadingState(ApiRequestStatusEnum.ERROR);
        }
      };

      fetchData();
    }, [fetchOpenViewAccountContacts, setLoadingState, leadId]);

    const initialValues: TFieldValues = useMemo(() => {
      return {
        houseNo: "",
        address1: "",
        address2: "",
        town: "",
        county: "",
        postcode: "",
        countryName: "",
        fullAddress: "",
        shortAddress: "",
        userId: openViewRelatedContactsOptions[0]?.value,
      };
    }, [openViewRelatedContactsOptions]);

    const isApplicant = [LeadTypeEnum.Sale, LeadTypeEnum.Let].includes(type);

    const validationSchema = useMemo(() => {
      return OpenviewValidationSchema(isApplicant);
    }, [isApplicant]);

    const personFullName = useMemo(() => {
      return (
        person?.fullName ||
        getUserFullName(person?.firstName, person?.lastName) ||
        ""
      );
    }, [person]);

    const {
      handleSubmit,
      control,
      formState: { isSubmitting },
      setValue,
      getValues,
      reset,
      watch,
    } = useForm<TFieldValues>({
      defaultValues: initialValues,
      mode: "onSubmit",
      resolver: yupResolver(validationSchema),
    });

    const currentUserId = watch("userId");

    useEffect(() => {
      setValue("userId", openViewRelatedContactsOptions[0]?.value);
    }, [openViewRelatedContactsOptions, setValue]);

    useEffect(() => {
      const current = getValues();
      reset(
        {
          ...current,
          ...transformedAddress,
        },
        { keepDirtyValues: true }
      );
    }, [reset, getValues, transformedAddress]);

    const onSubmit = useCallback(
      async (values: TFieldValues) => {
        const { userId, ...rest } = values;
        const selectedContact = openViewRelatedContactsOptions.find(
          user => user.value.toString() === userId
        );

        const isArchived = getArchivedState(type, selectedContact?.data);

        await handleSyncLead({
          address: rest,
          userId: userId === openViewNewContactValue ? null : userId,
          archived: isArchived,
        });
        closePrompt();
      },
      [type, openViewRelatedContactsOptions, handleSyncLead, closePrompt]
    );

    return (
      <ActionPromptContainer
        header={<Box color={"leadpro.600"}>Sync lead to iamproperty CRM</Box>}
        bodyStyle={{ padding: "0" }}
        body={
          <Box minHeight={"100px"} position={"relative"}>
            {loadingState === ApiRequestStatusEnum.PENDING && <Loader />}
            {loadingState === ApiRequestStatusEnum.ERROR && <Error />}
            {loadingState === ApiRequestStatusEnum.SUCCESS && (
              <Box>
                <Box
                  px={8}
                  py={5}
                  display={"flex"}
                  justifyContent={"space-between"}
                  alignItems={"center"}
                >
                  <Box>
                    {personFullName}{" "}
                    <Box display={"inline"} color={"leadpro.500"}>
                      ({person.email})
                    </Box>
                  </Box>
                  <LeadTypeTag type={type} />
                </Box>
                <hr />
                <Box px={8} pb={8}>
                  <form onSubmit={handleSubmit(onSubmit)}>
                    <LeadAddressForm
                      control={control}
                      isApplicant={isApplicant}
                      isValidating={
                        ecosystemLoadingState === ApiRequestStatusEnum.PENDING
                      }
                    />

                    <ExistingRecordsForm
                      control={control}
                      openViewRelatedContactsOptions={
                        openViewRelatedContactsOptions
                      }
                      currentUserId={currentUserId}
                      type={type}
                    />

                    <Divider mb={4} />
                    <Box
                      width={"100%"}
                      display={"flex"}
                      flexDirection={"row"}
                      justifyContent={"flex-end"}
                    >
                      <Button
                        type={"submit"}
                        colorScheme={"blue"}
                        isDisabled={isSubmitting}
                        isLoading={isSubmitting}
                      >
                        Sync to iamproperty CRM
                      </Button>
                    </Box>
                  </form>
                </Box>
              </Box>
            )}
          </Box>
        }
      />
    );
  }
);
